import React, { useEffect, useMemo, useRef, useState } from "react";
import {
 Box,
 Tooltip,
 Typography,
 Checkbox,
 FormControlLabel,
 IconButton,
 Button,
 Stack,
 CircularProgress,
 Dialog,
 DialogTitle,
 DialogContent,
 DialogActions,
 Chip,
 TextField,
 Select,
 MenuItem,
 ListItemText,
 FormControl,
} from "@mui/material";
import StyledDataGrid from "../../../components/StyledDataGrid";
import {
 fetchAliquotsPaged,
 fetchUniqueAliquotBarcodes,
 fetchSampleTypes,
} from "../../../utils/api";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import Badge from "@mui/material/Badge";
import { useNavigate } from "react-router-dom";
import { getSampleTypeFamily } from "../../../utils/sampleIds";
import { formatDateTimeInAppTimeZone } from "../../../utils/appDateTime";
import { BIOBANK_SUB_ROUTES } from "../../../constants/routes";
import { ALZHEIMERS_STATUS_OPTIONS } from "../../../constants/patientOptions";
import { BASE_CHIP_SX, getStatusChipStyle } from "../../../constants/statusColors";

const ALZHEIMERS_STATUS_LABELS = ALZHEIMERS_STATUS_OPTIONS.reduce(
  (acc, opt) => ({ ...acc, [opt.value]: opt.label }),
  {},
);

const valueOrFallback = (...values) => {
  const found = values.find((value) => value !== null && value !== undefined && String(value).trim() !== "");
  return found === undefined ? "N/A" : found;
};

const LocationCell = ({ location }) => {
  const fields = [
    ["Freezer", location?.freezer],
    ["Rack", location?.rack],
    ["Box", location?.box],
    ["Pos", location?.position],
  ];

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
        gap: 1,
        width: "100%",
        alignItems: "center",
        overflow: "hidden",
      }}
    >
      {fields.map(([label, value]) => (
        <Box
          key={label}
          title={`${label} ${valueOrFallback(value)}`}
          sx={{
            minWidth: 0,
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            overflow: "hidden",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              lineHeight: 1.2,
              whiteSpace: "nowrap",
            }}
          >
            {label}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontFamily: label === "Pos" ? "inherit" : "monospace",
              color: "text.primary",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              lineHeight: 1.2,
              minWidth: 0,
            }}
          >
            {valueOrFallback(value)}
          </Typography>
        </Box>
      ))}
    </Box>
  );
};

const AliquotsListTab = ({ mode }) => {
  const navigate = useNavigate();
  const isHistory = mode === 'history';
  const statusParam = isHistory ? 'Checked-Out' : 'Checked-In';

  const [checkoutDialogOpen, setCheckoutDialogOpen] = useState(false);
  const [checkoutSamples, setCheckoutSamples] = useState([]);
  const [selectionModel, setSelectionModel] = useState({ type: "include", ids: new Set() });
  const [selectedAliquotsById, setSelectedAliquotsById] = useState({});
  const sampleTypeMapRef = useRef({});
  const [qualityTypes] = useState(["Excellent", "Good", "Fair"]);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [pendingTypes, setPendingTypes] = useState([]);
  const [typeOpen, setTypeOpen] = useState(false);
  const [selectedQuality, setSelectedQuality] = useState([]);
  const [pendingQuality, setPendingQuality] = useState([]);
  const [qualityOpen, setQualityOpen] = useState(false);
  const [selectedAlzStatuses, setSelectedAlzStatuses] = useState([]);
  const [pendingAlzStatuses, setPendingAlzStatuses] = useState([]);
  const [alzStatusOpen, setAlzStatusOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [totalRowCount, setTotalRowCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [rows, setRows] = useState([]);
  const [formattedSampleTypes, setFormattedSampleTypes] = useState([]);
  const [uniqueBarcodes, setUniqueBarcodes] = useState([]);
  const [barcodesLoading, setBarcodesLoading] = useState(false);
  const [activeBarcodes, setActiveBarcodes] = useState([]);
  const [pendingBarcodes, setPendingBarcodes] = useState([]);
  const [selectOpen, setSelectOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const loadSampleTypes = async () => {
      try {
        const sampleTypesRes = await fetchSampleTypes();
        const typeMap = {};
        sampleTypesRes.forEach((st) => {
          typeMap[st.sampleLetter] = `${st.sampleLetter} - ${st.shortName}`;
        });
        sampleTypeMapRef.current = typeMap;
        setFormattedSampleTypes(sampleTypesRes.map((s) => `${s.sampleLetter} - ${s.shortName}`));
      } catch (err) {
        console.error("Failed to load sample types:", err);
      }
    };
    loadSampleTypes();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadBarcodes = async () => {
      setBarcodesLoading(true);
      try {
         const res = await fetchUniqueAliquotBarcodes(statusParam);
         let parsedBarcodes = [];
         if (Array.isArray(res)) parsedBarcodes = res;
         else if (res && Array.isArray(res.content)) parsedBarcodes = res.content;
         else if (res && Array.isArray(res.data)) parsedBarcodes = res.data;
         if (!cancelled) setUniqueBarcodes(parsedBarcodes);
      } catch (err) {
        console.error("Failed to fetch unique barcodes:", err);
      } finally {
        if (!cancelled) setBarcodesLoading(false);
      }
    };
    loadBarcodes();
    return () => { cancelled = true; };
  }, [statusParam]);

  const getTypeCodeFromLabel = (label) => {
    if (!label) return "";
    const [code] = String(label).split("-");
    return code ? code.trim().toUpperCase() : "";
  };

  const selectedTypeCodes = useMemo(
    () => selectedTypes.map((type) => getTypeCodeFromLabel(type)).filter(Boolean),
    [selectedTypes]
  );

  const formatRow = React.useCallback((a, index) => {
    const sampleTypeCode = getSampleTypeFamily(a.sampleType);
    return {
      id: a.aliquotId || `aliquot-${index}`,
      barcode: a.aliquotId || "N/A",
      patientAccountNumber: valueOrFallback(a.patientAccountNumber),
      alzheimersStatus: a.alzheimersStatus || null,
      quality: valueOrFallback(a.quality),
      location: {
        freezer: a.freezer,
        rack: a.rack,
        box: a.box,
        position: a.position,
      },
      coDateTime: a.coDateTime ? formatDateTimeInAppTimeZone(a.coDateTime, a.coDateTime) : "N/A",
      type: sampleTypeMapRef.current[sampleTypeCode] || sampleTypeCode || "N/A",
      aliquotData: a,
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadAliquots = async () => {
      try {
        setLoading(true);
        const response = await fetchAliquotsPaged({
          page: paginationModel.page,
          size: paginationModel.pageSize,
          status: statusParam,
          sampleTypes: selectedTypeCodes,
          qualities: selectedQuality,
          batchIds: activeBarcodes,
          alzheimersStatuses: selectedAlzStatuses,
        });
        if (cancelled) return;
        const aliquotsRes = Array.isArray(response?.content) ? response.content : [];
        setRows(aliquotsRes.map((a, i) => formatRow(a, i)));
        setTotalRowCount(response?.totalElements ?? 0);
      } catch (err) {
        console.error("Failed to load aliquots:", err);
        if (!cancelled) {
          setRows([]);
          setTotalRowCount(0);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadAliquots();
    return () => { cancelled = true; };
  }, [paginationModel.page, paginationModel.pageSize, selectedTypeCodes, selectedQuality, selectedAlzStatuses, activeBarcodes, formatRow, statusParam]);

  const extractSelectedIds = (model) => {
    if (!model) return [];
    if (Array.isArray(model)) return model.map((id) => String(id));
    if (model.ids && typeof model.ids.forEach === "function") return Array.from(model.ids, (id) => String(id));
    return [];
  };

  const normalizeSelectionModel = (model) => {
    if (Array.isArray(model)) return { type: "include", ids: new Set(model.map((id) => String(id))) };
    if (model?.ids && typeof model.ids.forEach === "function") return { type: model.type || "include", ids: new Set(Array.from(model.ids, (id) => String(id))) };
    return { type: "include", ids: new Set() };
  };

  const totalItemsInCart = extractSelectedIds(selectionModel).length;

  const columns = [
    { field: "barcode", headerName: "Aliquot Barcode ID", flex: 1 },
    { field: "patientAccountNumber", headerName: "Participant MRN", flex: 1 },
    {
      field: "alzheimersStatus",
      headerName: "Alzheimer's Status",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        if (!params.value) return "N/A";
        const label = ALZHEIMERS_STATUS_LABELS[params.value] || params.value;
        return (
          <Chip
            label={label}
            size="small"
            sx={{ ...BASE_CHIP_SX, ...getStatusChipStyle(params.value) }}
          />
        );
      },
    },
    { field: "quality", headerName: "BioSpecimen Quality", flex: 1 },
    ...(isHistory
      ? [{ field: "coDateTime", headerName: "Check-out Time", flex: 1.5 }]
      : [{
          field: "location",
          headerName: "Location",
          flex: 1.8,
          minWidth: 260,
          sortable: false,
          renderCell: (params) => <LocationCell location={params.row.location} />,
        }]),
  ];

  const handleClearFilters = () => {
    setSelectedTypes([]);
    setSelectedQuality([]);
    setSelectedAlzStatuses([]);
    setActiveBarcodes([]);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleCheckOut = () => {
    const selectedIds = extractSelectedIds(selectionModel);
    const selectedAliquots = selectedIds.map((id) => selectedAliquotsById[id]).filter(Boolean);
    setCheckoutSamples(selectedAliquots);
    setCheckoutDialogOpen(true);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
      {/* Top Filter Bar */}
      <Box
        sx={{ display: 'flex', gap: 2, mb: 2, mt: 1, alignItems: 'center', flexWrap: 'wrap' }}
        data-tour-id={mode === "checkout" ? "tour-aliquot-checkout-filter" : undefined}
      >
        <Badge badgeContent={activeBarcodes.length} sx={{ '& .MuiBadge-badge': { backgroundColor: '#500000', color: '#fff' } }}>
          <FormControl size="small" sx={{ width: 150 }}>
            <Select
              sx={{ 
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#500000' },
                ...(activeBarcodes.length > 0 && { backgroundColor: '#500000', color: '#fff', '& .MuiSvgIcon-root': { color: '#fff' } })
              }}
              multiple
              displayEmpty
              open={selectOpen}
              onOpen={() => { setPendingBarcodes(activeBarcodes); setSelectOpen(true); }}
              onClose={() => {
                setSelectOpen(false);
                if (JSON.stringify(pendingBarcodes) !== JSON.stringify(activeBarcodes)) {
                  setActiveBarcodes(pendingBarcodes);
                  setPaginationModel((prev) => ({ ...prev, page: 0 }));
                }
              }}
              value={selectOpen ? pendingBarcodes : activeBarcodes}
              onChange={(event) => {
                const { value } = event.target;
                if (value.includes('__clear__')) setPendingBarcodes([]);
                else setPendingBarcodes(typeof value === 'string' ? value.split(',') : value);
              }}
              renderValue={() => "Barcodes"}
              MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
            >
              {barcodesLoading ? (
                <MenuItem disabled><CircularProgress size={20} sx={{ mr: 2 }} />Loading...</MenuItem>
              ) : (
                [
                <MenuItem key="__clear__" value="__clear__" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>Clear Selection</MenuItem>,
                ...uniqueBarcodes.map((barcode) => {
                  const isChecked = (selectOpen ? pendingBarcodes : activeBarcodes).indexOf(barcode) > -1;
                  return (
                    <MenuItem key={barcode} value={barcode}>
                      <Checkbox checked={isChecked} size="small" sx={{ color: '#500000', '&.Mui-checked': { color: '#500000' } }} />
                      <ListItemText primary={barcode} primaryTypographyProps={{ fontFamily: 'monospace' }} />
                    </MenuItem>
                  );
                })
              ])}
            </Select>
          </FormControl>
        </Badge>

        <Badge badgeContent={selectedTypes.length} sx={{ '& .MuiBadge-badge': { backgroundColor: '#500000', color: '#fff' } }}>
          <FormControl size="small" sx={{ width: 150 }}>
            <Select
              sx={{ 
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#500000' },
                ...(selectedTypes.length > 0 && { backgroundColor: '#500000', color: '#fff', '& .MuiSvgIcon-root': { color: '#fff' } })
              }}
              multiple
              displayEmpty
              open={typeOpen}
              onOpen={() => { setPendingTypes(selectedTypes); setTypeOpen(true); }}
              onClose={() => {
                setTypeOpen(false);
                if (JSON.stringify(pendingTypes) !== JSON.stringify(selectedTypes)) {
                  setSelectedTypes(pendingTypes);
                  setPaginationModel((prev) => ({ ...prev, page: 0 }));
                }
              }}
              value={typeOpen ? pendingTypes : selectedTypes}
              onChange={(event) => {
                const { value } = event.target;
                if (value.includes('__clear__')) setPendingTypes([]);
                else setPendingTypes(typeof value === 'string' ? value.split(',') : value);
              }}
              renderValue={() => "Sample Type"}
            >
              {[
                <MenuItem key="__clear__" value="__clear__" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>Clear Selection</MenuItem>,
                ...formattedSampleTypes.map((type) => {
                const isChecked = (typeOpen ? pendingTypes : selectedTypes).indexOf(type) > -1;
                return (
                  <MenuItem key={type} value={type}>
                    <Checkbox checked={isChecked} size="small" sx={{ color: '#500000', '&.Mui-checked': { color: '#500000' } }} />
                    <ListItemText primary={type} />
                  </MenuItem>
                );
              })]}
            </Select>
          </FormControl>
        </Badge>

        <Badge badgeContent={selectedQuality.length} sx={{ '& .MuiBadge-badge': { backgroundColor: '#500000', color: '#fff' } }}>
          <FormControl size="small" sx={{ width: 150 }}>
            <Select
              sx={{ 
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#500000' },
                ...(selectedQuality.length > 0 && { backgroundColor: '#500000', color: '#fff', '& .MuiSvgIcon-root': { color: '#fff' } })
              }}
              multiple
              displayEmpty
              open={qualityOpen}
              onOpen={() => { setPendingQuality(selectedQuality); setQualityOpen(true); }}
              onClose={() => {
                setQualityOpen(false);
                if (JSON.stringify(pendingQuality) !== JSON.stringify(selectedQuality)) {
                  setSelectedQuality(pendingQuality);
                  setPaginationModel((prev) => ({ ...prev, page: 0 }));
                }
              }}
              value={qualityOpen ? pendingQuality : selectedQuality}
              onChange={(event) => {
                const { value } = event.target;
                if (value.includes('__clear__')) setPendingQuality([]);
                else setPendingQuality(typeof value === 'string' ? value.split(',') : value);
              }}
              renderValue={() => "Quality"}
            >
              {[
                <MenuItem key="__clear__" value="__clear__" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>Clear Selection</MenuItem>,
                ...qualityTypes.map((q) => {
                const isChecked = (qualityOpen ? pendingQuality : selectedQuality).indexOf(q) > -1;
                return (
                  <MenuItem key={q} value={q}>
                    <Checkbox checked={isChecked} size="small" sx={{ color: '#500000', '&.Mui-checked': { color: '#500000' } }} />
                    <ListItemText primary={q} />
                  </MenuItem>
                );
              })]}
            </Select>
          </FormControl>
        </Badge>

        <Badge badgeContent={selectedAlzStatuses.length} sx={{ '& .MuiBadge-badge': { backgroundColor: '#500000', color: '#fff' } }}>
          <FormControl size="small" sx={{ width: 180 }}>
            <Select
              sx={{
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#500000' },
                ...(selectedAlzStatuses.length > 0 && { backgroundColor: '#500000', color: '#fff', '& .MuiSvgIcon-root': { color: '#fff' } })
              }}
              multiple
              displayEmpty
              open={alzStatusOpen}
              onOpen={() => { setPendingAlzStatuses(selectedAlzStatuses); setAlzStatusOpen(true); }}
              onClose={() => {
                setAlzStatusOpen(false);
                if (JSON.stringify(pendingAlzStatuses) !== JSON.stringify(selectedAlzStatuses)) {
                  setSelectedAlzStatuses(pendingAlzStatuses);
                  setPaginationModel((prev) => ({ ...prev, page: 0 }));
                }
              }}
              value={alzStatusOpen ? pendingAlzStatuses : selectedAlzStatuses}
              onChange={(event) => {
                const { value } = event.target;
                if (value.includes('__clear__')) setPendingAlzStatuses([]);
                else setPendingAlzStatuses(typeof value === 'string' ? value.split(',') : value);
              }}
              renderValue={() => "Alzheimer's Status"}
            >
              {[
                <MenuItem key="__clear__" value="__clear__" sx={{ fontStyle: 'italic', color: 'text.secondary' }}>Clear Selection</MenuItem>,
                ...ALZHEIMERS_STATUS_OPTIONS.map((opt) => {
                  const isChecked = (alzStatusOpen ? pendingAlzStatuses : selectedAlzStatuses).indexOf(opt.value) > -1;
                  return (
                    <MenuItem key={opt.value} value={opt.value}>
                      <Checkbox checked={isChecked} size="small" sx={{ color: '#500000', '&.Mui-checked': { color: '#500000' } }} />
                      <ListItemText primary={opt.label} />
                    </MenuItem>
                  );
                })
              ]}
            </Select>
          </FormControl>
        </Badge>

        <Box sx={{ flexGrow: 1 }} />
        <Box sx={{ display: 'flex', gap: 2, ml: 'auto' }}>
          <Button 
            variant="outlined" 
            onClick={handleClearFilters}
            sx={{ 
              color: '#500000', bgcolor: '#fff', borderColor: '#500000', height: '40px', textTransform: 'uppercase', borderRadius: 0, px: 3,
              '&:hover': { bgcolor: '#f5f5f5', borderColor: '#3a0000', color: '#3a0000' }
            }}
          >
            Clear
          </Button>
          <Button 
            variant="contained" 
            onClick={() => setPaginationModel((prev) => ({ ...prev, page: 0 }))}
            sx={{ 
              bgcolor: '#500000', color: '#fff', height: '40px', textTransform: 'uppercase', borderRadius: 0, px: 3,
              '&:hover': { bgcolor: '#3a0000' }
            }}
          >
            Search
          </Button>
        </Box>

        {!isHistory && (
          <Button
            variant="contained"
            disabled={totalItemsInCart === 0}
            onClick={handleCheckOut}
            startIcon={
              <Badge badgeContent={totalItemsInCart} sx={{ '& .MuiBadge-badge': { backgroundColor: '#500000', color: '#fff' } }}>
                <ShoppingCartIcon />
              </Badge>
            }
            sx={{ 
              marginRight: "8px", px: 3, height: '40px', bgcolor: '#500000', color: '#fff',
              '&:hover': { bgcolor: '#3a0000' },
              '&.Mui-disabled': { bgcolor: '#e0e0e0', color: '#9e9e9e' }
            }}
          >
            CHECK-OUT SELECTED
          </Button>
        )}
      </Box>

      {/* DataGrid Panel */}
      <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
          <StyledDataGrid
            rows={rows}
            columns={columns}
            loading={loading}
            pagination
            paginationMode="server"
            rowCount={totalRowCount}
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 25, 50]}
            checkboxSelection={!isHistory}
            onRowSelectionModelChange={(item) => {
              if (isHistory) return;
              const normalizedModel = normalizeSelectionModel(item);
              setSelectionModel(normalizedModel);
              const selectedIds = normalizedModel.ids;
              setSelectedAliquotsById((prev) => {
                const next = { ...prev };
                rows.forEach((row) => {
                  const rowId = String(row.id);
                  if (selectedIds.has(rowId) && row.aliquotData) {
                    next[rowId] = row.aliquotData;
                  }
                });
                Object.keys(next).forEach((id) => {
                  if (!selectedIds.has(id)) delete next[id];
                });
                return next;
              });
            }}
            rowSelectionModel={!isHistory ? selectionModel : undefined}
            keepNonExistentRowsSelected={!isHistory}
          />
        </Box>
      </Box>

      {!isHistory && (
        <Dialog open={checkoutDialogOpen} onClose={() => setCheckoutDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Confirm Check-Out</DialogTitle>
          <DialogContent>
            <Typography>You are about to check out {checkoutSamples.length} samples.</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setCheckoutDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={() => {
              setCheckoutDialogOpen(false);
              navigate(BIOBANK_SUB_ROUTES.ALIQUOT_MANAGEMENT_STORAGE_LOCATION, {
                state: {
                  samples: checkoutSamples,
                  returnTo: BIOBANK_SUB_ROUTES.ALIQUOT_MANAGEMENT_CHECK_OUT,
                },
              });
            }}>Confirm</Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};
export default AliquotsListTab;
